import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'ro' | 'hu' | 'rom';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  translations: Record<string, any>;
  loading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('ro');
  const [translations, setTranslations] = useState<Record<string, any>>({});
  const [fallbackTranslations, setFallbackTranslations] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);

  // Load Romanian translations once as fallback
  useEffect(() => {
    const loadFallback = async () => {
      try {
        const response = await fetch('/translations/ro.json');
        if (response.ok) {
          const data = await response.json();
          setFallbackTranslations(data);
        } else {
          console.error('Failed to load fallback Romanian translations');
        }
      } catch (error) {
        console.error('Error loading fallback Romanian translations:', error);
      }
    };

    loadFallback();
  }, []);

  // Load translations from JSON files
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/translations/${language}.json`);
        if (response.ok) {
          const data = await response.json();
          setTranslations(data);
        } else {
          console.error(`Failed to load translations for ${language}`);
          // Fallback to Romanian if translation fails
          if (language !== 'ro') {
            const fallbackResponse = await fetch('/translations/ro.json');
            if (fallbackResponse.ok) {
              const fallbackData = await fallbackResponse.json();
              setTranslations(fallbackData);
            }
          }
        }
      } catch (error) {
        console.error('Error loading translations:', error);
        // Fallback to Romanian
        if (language !== 'ro') {
          try {
            const fallbackResponse = await fetch('/translations/ro.json');
            if (fallbackResponse.ok) {
              const fallbackData = await fallbackResponse.json();
              setTranslations(fallbackData);
            }
          } catch (fallbackError) {
            console.error('Error loading fallback translations:', fallbackError);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    loadTranslations();
  }, [language]);

  // Save language preference to localStorage
  useEffect(() => {
    localStorage.setItem('preferred_language', language);
  }, [language]);

  // Update HTML lang attribute and meta tags when language changes
  useEffect(() => {
    // Map language codes to HTML lang codes
    const langMap: Record<Language, string> = {
      'en': 'en',
      'ro': 'ro',
      'hu': 'hu',
      'rom': 'rom' // Romany/Romani
    };

    const htmlLang = langMap[language] || 'ro';
    
    // Update HTML lang attribute
    document.documentElement.lang = htmlLang;

    // Update or create Content-Language meta tag
    let contentLangMeta = document.querySelector('meta[http-equiv="Content-Language"]');
    if (!contentLangMeta) {
      contentLangMeta = document.createElement('meta');
      contentLangMeta.setAttribute('http-equiv', 'Content-Language');
      document.head.appendChild(contentLangMeta);
    }
    contentLangMeta.setAttribute('content', htmlLang);

    // Update or create notranslate meta tag
    let notranslateMeta = document.querySelector('meta[name="google"]');
    if (!notranslateMeta) {
      notranslateMeta = document.createElement('meta');
      notranslateMeta.setAttribute('name', 'google');
      document.head.appendChild(notranslateMeta);
    }
    notranslateMeta.setAttribute('content', 'notranslate');
  }, [language]);

  // Load language preference from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred_language') as Language;
    if (savedLanguage && ['en', 'ro', 'hu', 'rom'].includes(savedLanguage)) {
      setLanguageState(savedLanguage);
    } else {
      // Default to Romanian if no saved preference
      setLanguageState('ro');
    }
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  // Translation function with nested key support
  const t = (key: string): string => {
    const keys = key.split('.');
    const resolve = (source: Record<string, any>) => {
      let value: any = source;
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          return undefined;
        }
      }
      return value;
    };

    const primary = resolve(translations);
    if (typeof primary === 'string') {
      return primary;
    }

    const fallback = resolve(fallbackTranslations);
    if (typeof fallback === 'string') {
      return fallback;
    }

    return key;
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    translations,
    loading,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
